import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import { useNavigate  } from 'react-router-dom'
import {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrandById,
  setCreateBrand,
  setUpdateBrand,
  setDeleteBrandById,
} from '../../redux/actions/brand';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import BrandModal from './subcomponents/BrandModal';
import CustomBreadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { hasPermission } from '../../utils/permissions';

function BrandList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listState = useSelector((state) => state.brand.get);
  const createState = useSelector((state) => state.brand.create);
  const updateState = useSelector((state) => state.brand.update);
  const deleteState = useSelector((state) => state.brand.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getBrands());
  }, [dispatch]);

  const handleCreate = useCallback(
    (name) => {
      setLoading(true);
      dispatch(createBrand(name));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, name) => {
      setLoading(true);
      dispatch(updateBrand(id, name));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteBrandById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingBrand(null);
    setIsEditModalOpen(false);
  };

  const onBrandCreate = (name) => {
    handleCreate(name);
    closeCreateModal();
  };

  const onBrandEdit = (name) => {
    if (editingBrand) {
      handleEdit(editingBrand.id, name);
      closeEditModal();
    }
  };

  const navigateToModels = (brandId, brandName) => {
    console.log(
      'navigateToModels - brandId:',
      brandId,
      'brandName:',
      brandName,
    );
    navigate(`/brands/${brandId}/models`, { state: { brandName } });
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = 'BGL - Marcas';
    // Solo carga inicial
    setLoading(true);
    dispatch(getBrands());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getBrands());
      }
      clearReduxState(setCreateBrand);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getBrands());
      }
      clearReduxState(setUpdateBrand);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getBrands());
      }
      clearReduxState(setDeleteBrandById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('brands.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear marca</span>
            </Button>
          )}
          <Button
            className="float-right mb-2 mr-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Marcas</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Modelos</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center">
                  No hay marcas disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td>
                    <span className="badge badge-info">
                      {row.models_count || 0} modelos
                    </span>
                  </td>
                  <td>
                    <>
                      <Button
                        className="mr-2"
                        size="sm"
                        color="primary"
                        onClick={() => navigateToModels(row.id, row.name)}
                      >
                        <i className="fa fa-list mr-1"></i>
                        Ver Modelos
                      </Button>
                      {hasPermission('brands.edit') && (
                        <Button
                          className="mr-2"
                          size="sm"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('brands.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger btn-sm"
                          iconClass="fa fa-trash"
                          title="Eliminar marca"
                          message="¿Estás seguro que quieres eliminar esta marca? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Brand Modal */}
      <BrandModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear marca"
        onSubmit={onBrandCreate}
      />

      {/* Edit Brand Modal */}
      <BrandModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar marca"
        defaultBrand={editingBrand}
        onSubmit={onBrandEdit}
      />
    </>
  );
}

export default BrandList;
